import { Children, isValidElement, useState, type CSSProperties, type ReactNode } from 'react'
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import { SegmentedControlItem, type SegmentedControlItemProps } from './nested/item'
import type { PartProps } from '../../lib/part-props'

/**
 * One height sets the track and everything follows: an inset of h/10 rounded to the hairline so
 * the slab's edges land on device pixels, the outer corner h/2, and the inner one the outer less
 * the inset so the two stay concentric. Two sizes at one type step and one icon size, because size
 * here is density, not emphasis.
 */
const SIZES = {
  sm: '[--segmented-h:24px] [--segmented-pad:8px]',
  md: '[--segmented-h:28px] [--segmented-pad:10px]',
}

const segmentedControlVariants = cva(
  [
    'relative grid-flow-col auto-cols-[minmax(var(--segmented-h),1fr)] select-none',
    '[--segmented-inset:round(calc(var(--segmented-h)/10),var(--border-hairline))] [--segmented-radius:calc(var(--segmented-h)/2)]',
    'h-[var(--segmented-h)] p-[var(--segmented-inset)] bg-element corner-squircle rounded-[var(--segmented-radius)]',
    // The slab's two edges follow the index on the transition in base.css.
    '[--segmented-lead:var(--segmented-index)] [--segmented-trail:var(--segmented-index)]',
  ],
  {
    variants: {
      size: SIZES,
      // Sizes to its widest segment times its count, like a button sizes to its label; block fills
      // the row instead.
      block: { true: 'grid w-full', false: 'inline-grid' },
    },
    defaultVariants: { size: 'md', block: false },
  },
)

interface SegmentedControlProps
  extends Omit<PartProps<BaseRadioGroup.Props<string>>, 'style'>, VariantProps<typeof segmentedControlVariants> {
  className?: string
  style?: CSSProperties
  children?: ReactNode
}

/**
 * A pick-one strip for switching a view or a setting in place: a theme, a layout, a period. One tab
 * stop, arrows move and choose, as a radio group. For more than five options, or options that need
 * a sentence each, use a Select or a RadioGroup.
 *
 * The slab is 1/n of the rail and travels its own width per step, so any count from two to six
 * works. It reads the chosen index off the items, so they are this component's direct children.
 */
function SegmentedControl({
  size,
  block,
  value,
  defaultValue,
  onValueChange,
  className,
  style,
  children,
  ...props
}: SegmentedControlProps) {
  const [uncontrolled, setUncontrolled] = useState(defaultValue)
  const chosen = value ?? uncontrolled
  const values = Children.toArray(children).flatMap((child) =>
    isValidElement<SegmentedControlItemProps>(child) && child.props.value !== undefined ? [child.props.value] : [],
  )
  const index = values.indexOf(chosen as string)

  return (
    <BaseRadioGroup<string>
      data-slot="segmented-control"
      value={chosen}
      onValueChange={(next, details) => {
        setUncontrolled(next)
        onValueChange?.(next, details)
      }}
      className={twMerge(segmentedControlVariants({ size, block }), className)}
      style={
        {
          '--segmented-count': Math.max(values.length, 1),
          '--segmented-index': Math.max(index, 0),
          ...style,
        } as CSSProperties
      }
      {...props}
    >
      {children}
      <span
        aria-hidden
        data-slot="segmented-control-indicator"
        className={twMerge(
          'pointer-events-none absolute inset-y-[var(--segmented-inset)] left-[var(--segmented-inset)]',
          'w-[calc((100%-2*var(--segmented-inset))/var(--segmented-count))]',
          'bg-background-raised corner-squircle shadow-xs rounded-[calc(var(--segmented-radius)-var(--segmented-inset))]',
          // translate and scale, never left and width, so the move costs no layout. The midpoint of
          // the two edges is the slab's centre; their separation is how much longer than a step it is.
          '[translate:calc((var(--segmented-lead)+var(--segmented-trail))/2*100%)_0]',
          '[scale:calc(1+max(var(--segmented-lead)-var(--segmented-trail),var(--segmented-trail)-var(--segmented-lead)))_1]',
          // Nothing chosen yet is a real state for a filter strip, so the slab is simply not there.
          index < 0 && 'opacity-0',
        )}
      />
    </BaseRadioGroup>
  )
}

SegmentedControl.Item = SegmentedControlItem

export { SegmentedControl, type SegmentedControlProps }
